import React, {useContext, useEffect, useState} from "react"
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material"
import {LoadingButton} from "@mui/lab"
import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import {
    useCreateAiTemplateMutation,
    useDeleteAiTemplateMutation,
    useDeleteAiTemplateTranslationMutation,
    useGetAiTemplatesQuery,
    useSaveAiTemplateTranslationMutation,
    useUpdateAiTemplateMutation,
    useUpdateAiTemplateRuleMutation
} from "../../core/api/ai-zendesk-settings-api"
import {errorText, formatExact, ruleKeyLabel} from "../../components/AiTickets/aiTicketsUtils"
import {ConfirmDialog} from "../../components/AiTickets/DecisionDialogs"
import {SnackBarContext} from "../../snackBarProvider"
import TextEditDialog from "./TextEditDialog"

const textBoxSx = {whiteSpace: "pre-wrap", wordBreak: "break-word"}

const audit = (updatedAt, updatedBy) =>
    updatedAt ? `${formatExact(updatedAt)}${updatedBy ? ` by ${updatedBy}` : ""}` : null

const GAMES = [["", "any game"], ["clockmaker", "Clockmaker"], ["toh", "Train of Hope"], ["diamond_sort", "Diamond Sort"], ["cube_match", "Cube Match"]]
const gameLabel = (game) => GAMES.find(([v]) => v === (game ?? ""))?.[1] ?? game

// when the bot picks the template on its own (restriction scenario, after a Hermes check)
const KINDS = [
    ["", "never (manual use only)"],
    ["CHEATER", "anticheat ban with one of the reason codes"],
    ["CHAT_MUTE", "active chat mute ({until} = when it ends)"]
]
const KEY_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/

const parseCodes = (codes) => codes.split(/[\s,]+/).map((c) => c.trim()).filter(Boolean)

/** Rule fields shared by "Edit rule" and "Add template": kind, game and (for CHEATER) Hermes ban reason codes. */
const RuleFields = ({kind, setKind, game, setGame, codes, setCodes}) => (
    <>
        <TextField select label="Bot sends it when" size="small" fullWidth value={kind} onChange={(e) => setKind(e.target.value)} sx={{mb: 2}}>
            {KINDS.map(([value, label]) => <MenuItem key={value || "none"} value={value}>{label}</MenuItem>)}
        </TextField>
        {kind && (
            <TextField select label="Game" size="small" value={game} onChange={(e) => setGame(e.target.value)} sx={{mb: 2, minWidth: 220}}>
                {GAMES.map(([value, label]) => <MenuItem key={value || "any"} value={value}>{label}</MenuItem>)}
            </TextField>
        )}
        {kind === "CHEATER" && (
            <TextField
                label="Ban reason codes (one per line)"
                fullWidth multiline minRows={4} maxRows={14} size="small"
                value={codes} onChange={(e) => setCodes(e.target.value)}
                helperText={`${parseCodes(codes).length} code(s), exactly as the game backend reports them`}
            />
        )}
        {kind === "CHAT_MUTE" && (
            <DialogContentText variant="body2">
                Write {"{until}"} in the English text where the mute end date belongs; it is filled in for every language.
            </DialogContentText>
        )}
    </>
)

const ruleValid = (kind, codes) => kind !== "CHEATER" || parseCodes(codes).length > 0
const rulePayload = (kind, game, codes) => ({
    restrictionKind: kind || null,
    game: kind ? game || null : null,
    reasons: kind === "CHEATER" ? parseCodes(codes) : []
})

/** Rule editor: when the bot answers with this template on its own. */
const RuleEditDialog = ({open, template, loading, onClose, onSave}) => {
    const [kind, setKind] = useState("")
    const [game, setGame] = useState("")
    const [codes, setCodes] = useState("")
    useEffect(() => {
        if (open) {
            setKind(template?.restrictionKind ?? ((template?.restrictionReasons ?? []).length > 0 ? "CHEATER" : ""))
            setGame(template?.game ?? "")
            setCodes((template?.restrictionReasons ?? []).join("\n"))
        }
    }, [open, template])
    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>When the bot sends: {ruleKeyLabel(template?.key)}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{mb: 2}}>
                    The bot checks the player in Hermes and prepares this template as a draft for the operator (same approval
                    flow as any other reply, nothing is sent automatically).
                </DialogContentText>
                <RuleFields kind={kind} setKind={setKind} game={game} setGame={setGame} codes={codes} setCodes={setCodes}/>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" disabled={loading}>Cancel</Button>
                <LoadingButton onClick={() => onSave(rulePayload(kind, game, codes))} variant="contained" loading={loading}
                               disabled={!ruleValid(kind, codes)}>
                    Save rule
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

/** New template: key, English text and an optional rule. */
const CreateTemplateDialog = ({open, existingKeys, loading, onClose, onSave}) => {
    const [key, setKey] = useState("")
    const [text, setText] = useState("")
    const [kind, setKind] = useState("")
    const [game, setGame] = useState("")
    const [codes, setCodes] = useState("")
    useEffect(() => {
        if (open) {
            setKey("")
            setText("")
            setKind("")
            setGame("")
            setCodes("")
        }
    }, [open])
    const normalizedKey = key.trim().toLowerCase()
    const keyError = normalizedKey && !KEY_RE.test(normalizedKey)
        ? "lowercase letters, digits and dashes only"
        : existingKeys.includes(normalizedKey) ? "this key already exists" : null
    const canSave = !!normalizedKey && !keyError && !!text.trim() && ruleValid(kind, codes)
    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>New template</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{mb: 2}}>
                    Write the English text without a greeting: the bot adds "Hello! Thank you for reaching out." itself in the
                    first message of a dialog. Translations appear after the first player in each language.
                </DialogContentText>
                <TextField
                    label="Key" size="small" fullWidth sx={{mb: 2}} placeholder="restriction-cheater-toh-arena"
                    value={key} onChange={(e) => setKey(e.target.value)}
                    error={!!keyError} helperText={keyError ?? "identifier shown in ticket history, cannot be changed later"}
                />
                <TextField
                    label="English text" fullWidth multiline minRows={5} maxRows={16} size="small" sx={{mb: 2}}
                    value={text} onChange={(e) => setText(e.target.value)}
                />
                <RuleFields kind={kind} setKind={setKind} game={game} setGame={setGame} codes={codes} setCodes={setCodes}/>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" disabled={loading}>Cancel</Button>
                <LoadingButton onClick={() => onSave({key: normalizedKey, text: text.trim(), ...rulePayload(kind, game, codes)})}
                               variant="contained" loading={loading} disabled={!canSave}>
                    Create
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

/** One-line description of the rule for the card */
const ruleSummary = (template) => {
    const kind = template.restrictionKind ?? ((template.restrictionReasons ?? []).length > 0 ? "CHEATER" : null)
    if (kind === "CHEATER") return `Draft for ${gameLabel(template.game)} when the Hermes ban reason is:`
    if (kind === "CHAT_MUTE") return `Draft for ${gameLabel(template.game)} when a chat mute is active ({until} = end of the mute)`
    return "Not sent by the bot on its own (no rule)"
}

/**
 * Bot reply templates (greeting, escalation notice, night notice, ...): the English original and translations.
 * Manual translations (HUMAN) win over machine ones (LLM); machine translations are redone when the original changes.
 */
export default function TemplatesEditor() {
    const {addAlert} = useContext(SnackBarContext)
    const {data: templates = [], isFetching, error} = useGetAiTemplatesQuery()
    const [updateTemplate, {isLoading: savingOriginal}] = useUpdateAiTemplateMutation()
    const [saveTranslation, {isLoading: savingTranslation}] = useSaveAiTemplateTranslationMutation()
    const [deleteTranslation, {isLoading: deleting}] = useDeleteAiTemplateTranslationMutation()
    const [updateRule, {isLoading: savingRule}] = useUpdateAiTemplateRuleMutation()
    const [createTemplate, {isLoading: creating}] = useCreateAiTemplateMutation()
    const [deleteTemplate, {isLoading: deletingTemplate}] = useDeleteAiTemplateMutation()
    // template whose rule is being edited
    const [ruleEditing, setRuleEditing] = useState(null)
    const [adding, setAdding] = useState(false)
    // template (created from the panel) about to be deleted
    const [removing, setRemoving] = useState(null)
    // {mode: "original" | "translation" | "new", key, language?, text?}
    const [editing, setEditing] = useState(null)
    // {key, language}
    const [resetting, setResetting] = useState(null)

    const run = async (action, successText) => {
        try {
            await action().unwrap()
            addAlert({text: successText, type: "success"})
            return true
        } catch (e) {
            addAlert({text: errorText(e), type: "error"})
            return false
        }
    }

    const onSave = async ({text, language}) => {
        const {mode, key} = editing
        const ok = mode === "original"
            ? await run(() => updateTemplate({key, text}), "Template updated: machine translations will be redone")
            : await run(() => saveTranslation({key, language: mode === "new" ? language : editing.language, text}), "Translation saved")
        if (ok) setEditing(null)
    }

    const onReset = async () => {
        const {key, language} = resetting
        if (await run(() => deleteTranslation({key, language}), `Translation removed: "${language}" will be translated by machine`)) {
            setResetting(null)
        }
    }

    if (error) return <Alert severity="error">{errorText(error)}</Alert>
    if (isFetching && templates.length === 0) return <CircularProgress/>

    return (
        <Stack spacing={2}>
            <Stack direction="row" sx={{alignItems: "flex-start", justifyContent: "space-between", gap: 2}}>
                <Typography variant="body2" color="text.secondary">
                    Fixed texts the bot sends. The English original is translated to the player's language by machine once per
                    language; write a manual translation to control the wording (e.g. informal "du" in German). A manual
                    translation is marked "outdated" when the original changes and is kept until you edit or remove it.
                    A template with a rule is prepared as a draft after a Hermes check; every draft still waits for the operator.
                </Typography>
                <Button variant="contained" size="small" startIcon={<AddIcon/>} sx={{flexShrink: 0}} onClick={() => setAdding(true)}>
                    Add template
                </Button>
            </Stack>
            {templates.map(({template, builtIn, translations}) => (
                <Card key={template.key} variant="outlined">
                    <CardContent>
                        <Stack direction="row" sx={{alignItems: "center", justifyContent: "space-between", mb: 1}}>
                            <Box>
                                <Typography variant="subtitle1">{ruleKeyLabel(template.key)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {template.key}{audit(template.updatedAt, template.updatedBy) ? ` · edited ${audit(template.updatedAt, template.updatedBy)}` : ""}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5} sx={{flexShrink: 0}}>
                                <Button size="small" startIcon={<EditOutlinedIcon/>}
                                        onClick={() => setEditing({mode: "original", key: template.key, text: template.textEn})}>
                                    Edit English
                                </Button>
                                {!builtIn && (
                                    <Tooltip title="Delete this template and its translations">
                                        <IconButton size="small" color="error" onClick={() => setRemoving(template)}>
                                            <DeleteOutlineIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Stack>
                        <Typography variant="body2" sx={{...textBoxSx, p: 1.5, bgcolor: "grey.100", borderRadius: 1}}>
                            {template.textEn}
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{alignItems: "center", flexWrap: "wrap", rowGap: 0.5, mt: 1}}>
                            <Typography variant="caption" color="text.secondary">{ruleSummary(template)}</Typography>
                            {(template.restrictionReasons ?? []).map((code) => <Chip key={code} size="small" label={code} sx={{fontFamily: "monospace"}}/>)}
                            <Button size="small" startIcon={<EditOutlinedIcon/>} onClick={() => setRuleEditing(template)}>Edit rule</Button>
                        </Stack>
                        <Table size="small" sx={{mt: 1}}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{width: 70}}>Lang</TableCell>
                                    <TableCell>Translation</TableCell>
                                    <TableCell sx={{width: 170}}>Source</TableCell>
                                    <TableCell sx={{width: 90}}/>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {translations.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            <Typography variant="body2" color="text.secondary">
                                                No translations yet: they appear after the first player in that language.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {translations.map((t) => (
                                    <TableRow key={t.language}>
                                        <TableCell>{t.language}</TableCell>
                                        <TableCell><Typography variant="body2" sx={textBoxSx}>{t.text}</Typography></TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} sx={{flexWrap: "wrap"}}>
                                                <Tooltip title={audit(t.updatedAt, t.updatedBy) ?? ""}>
                                                    <Chip size="small" label={t.source === "HUMAN" ? "manual" : "machine"}
                                                          color={t.source === "HUMAN" ? "success" : "default"}/>
                                                </Tooltip>
                                                {t.stale && (
                                                    <Tooltip title={t.source === "HUMAN"
                                                        ? "The English original changed after this translation was written"
                                                        : "Will be translated again on next use"}>
                                                        <Chip size="small" label="outdated" color="warning"/>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Edit translation (saved as manual)">
                                                <IconButton size="small"
                                                            onClick={() => setEditing({mode: "translation", key: template.key, language: t.language, text: t.text})}>
                                                    <EditOutlinedIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Remove: machine-translate again on next use">
                                                <IconButton size="small" onClick={() => setResetting({key: template.key, language: t.language})}>
                                                    <RestartAltIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Button size="small" startIcon={<AddIcon/>} sx={{mt: 1}}
                                onClick={() => setEditing({mode: "new", key: template.key, text: ""})}>
                            Add translation
                        </Button>
                    </CardContent>
                </Card>
            ))}
            <TextEditDialog
                open={!!editing}
                title={editing?.mode === "original"
                    ? `English original: ${ruleKeyLabel(editing?.key)}`
                    : editing?.mode === "new"
                        ? `New translation: ${ruleKeyLabel(editing?.key)}`
                        : `Translation (${editing?.language}): ${ruleKeyLabel(editing?.key)}`}
                hint={editing?.mode === "original"
                    ? "Machine translations will be redone on next use; manual translations will be marked outdated."
                    : "Saved as a manual translation: the bot will send exactly this text to players in this language."}
                initialText={editing?.text ?? ""}
                askLanguage={editing?.mode === "new"}
                loading={savingOriginal || savingTranslation}
                onClose={() => setEditing(null)}
                onSave={onSave}
            />
            <RuleEditDialog
                open={!!ruleEditing}
                template={ruleEditing}
                loading={savingRule}
                onClose={() => setRuleEditing(null)}
                onSave={async (rule) => {
                    if (await run(() => updateRule({key: ruleEditing.key, ...rule}), "Rule saved")) setRuleEditing(null)
                }}
            />
            <CreateTemplateDialog
                open={adding}
                existingKeys={templates.map(({template}) => template.key)}
                loading={creating}
                onClose={() => setAdding(false)}
                onSave={async (body) => {
                    if (await run(() => createTemplate(body), `Template "${body.key}" created`)) setAdding(false)
                }}
            />
            <ConfirmDialog
                open={!!removing}
                title="Delete template?"
                text={`"${removing?.key}" and its translations will be deleted. Drafts already prepared with it stay in the ticket history.`}
                confirmText="Delete"
                loading={deletingTemplate}
                onClose={() => setRemoving(null)}
                onConfirm={async () => {
                    if (await run(() => deleteTemplate({key: removing.key}), "Template deleted")) setRemoving(null)
                }}
            />
            <ConfirmDialog
                open={!!resetting}
                title="Remove translation?"
                text={`The "${resetting?.language}" translation will be deleted and the template will be translated by machine on next use.`}
                confirmText="Remove"
                loading={deleting}
                onClose={() => setResetting(null)}
                onConfirm={onReset}
            />
        </Stack>
    )
}
